import { computed, nextTick, ref, Ref, watch } from "@vue/composition-api";
import { FileData } from "@scrile/api-provider/dist/api/FileProvider";
import { MessageAttachment } from "@scrile/api-provider/dist/api/MessagesProvider";
import usePlayer from "@/hooks/usePlayer";
import { isVideoFile } from "@/lib/fileHelpers";
import { MediaFilesController } from "@/lib/MediaFilesController";
import { MAIN_CONTAINER_WIDTH } from "@/const";
import { pauseGlobalPlayer } from "@/components/GlobalAudioPlayer/helpers";

export default function useVideoAttachment(
  attachment: Ref<MessageAttachment | FileData>,
  isActive: Ref<boolean>,
  isFullScreen: Ref<boolean>,
  isProcessed: Ref<boolean>,
  videoStartTime: Ref<number>
) {
  const videoElement = ref<HTMLVideoElement>();
  const { play, pause: pauseVideo, isMediaPlaying: videoPlaying, setVideoStartTime } = usePlayer(videoElement);
  const videoStarted = ref(isActive.value && videoStartTime.value > 0);
  const file = computed(() => ("file" in attachment.value ? attachment.value.file : attachment.value));
  const isVideo = computed(() => isVideoFile(file.value));
  const videoSrc = computed(() => {
    if (!isVideo.value) return "";
    return MediaFilesController.getVideoUrl(file.value.urlPart);
  });

  const dpr = Math.floor(window.devicePixelRatio);
  const previewThumbnail = computed(() => {
    if (!isVideo.value) return "";
    return MediaFilesController.getVideoThumbnail(file.value.urlPart, MAIN_CONTAINER_WIDTH * dpr, 0, {
      exactSize: true,
    });
  });
  const fullScreenThumbnail = computed(() => {
    if (!isVideo.value) return "";
    return MediaFilesController.getVideoThumbnail(file.value.urlPart, "1080p");
  });
  const videoThumbnail = computed(() => (isFullScreen.value ? fullScreenThumbnail.value : previewThumbnail.value));

  const playVideo = async () => {
    videoStarted.value = true;
    await nextTick();
    play();
    pauseGlobalPlayer();
  };

  const stopVideo = () => {
    pauseVideo();
    videoStarted.value = false;
  };

  const shouldPlayVideo = computed(() => !videoPlaying.value && isVideo.value && isProcessed.value);

  watch(isActive, () => {
    if (!isActive.value) stopVideo();
  });

  return {
    isVideo,
    videoSrc,
    videoThumbnail,
    videoStarted,
    videoPlaying,
    shouldPlayVideo,
    playVideo,
    pauseVideo,
    videoElement,
    setVideoStartTime,
  };
}
